import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Modal from '../Modal/Modal';
import LoadingButton from '../../functional/LoadingButton/LoadingButton';
import TextInput from '../../functional/TextInput/TextInput';
import SelectInput from '../../functional/SelectInput/SelectInput';
import CheckboxInput from '../../functional/CheckboxInput/CheckboxInput';

interface OrganizationProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: OrganizationProfileData) => void;
}

export interface OrganizationProfileData {
  customerName: string;
  customerEmail: string;
  primaryRole?: string;
  organizationName: string;
  cityState: string;
  areaOfFocus: string[];
  taxExemptStatus?: string;
  targetPopulations: string[];
  organizationSize?: string;
  howHeardAbout?: string;
}

const roleOptions = [
  'Founder',
  'Executive Leadership', 
  'Board Member',
  'Program/Service Delivery',
  'Fundraising and Development',
  'Finance and Administration',
  'Marketing and Communications',
  'Data and Evaluation',
  'Consulting Support',
  'Other'
];

const focusAreaOptions = [
  'Education',
  'Health and Wellness',
  'Social Services',
  'Arts and Culture',
  'Environment and Conservation',
  'Human Rights and Advocacy',
  'Community Development',
  'Youth Development',
  'International Aid and Development',
  'Animal Welfare',
  'Faith-Based and Spiritual',
  'Research and Policy'
];

const taxStatusOptions = [
  'None- Grassroots',
  'None- Application in Process',
  'Pending- Application Submitted',
  '501(c)(3)',
  '501(c)(4)',
  '501(c)(7)',
  '501(c)(8)',
  '501(c)(10)',
  'Other'
];

const targetPopulationOptions = [
  'Infants and toddlers',
  'School-aged children',
  'Adolescents and teens',
  'Young adults',
  'Low-income families',
  'Single-parent households',
  'Families experiencing homelessness or housing instability',
  'Families affected by domestic violence or substance abuse',
  'Elderly individuals in need of support or care',
  'Aging populations facing isolation or health challenges',
  'Caregivers for seniors',
  'Individuals with physical disabilities',
  'People with intellectual or developmental disabilities',
  'Those with sensory impairments (e.g., vision, hearing)',
  'Individuals living below the poverty line',
  'Unemployed or underemployed persons',
  'Homeless individuals',
  'Racial and ethnic minorities',
  'Indigenous peoples',
  'Immigrants and refugees',
  'LGBTQ+ communities',
  'Survivors of domestic violence or sexual assault',
  'Women in poverty or at risk',
  'Girls and young women needing mentorship or education',
  'Active-duty military personnel',
  'Veterans transitioning to civilian life',
  'Families of military members',
  'People impacted by the criminal justice system',
  'Formerly incarcerated individuals re-entering society',
  'Juvenile offenders',
  'Individuals with chronic illnesses',
  'People living with HIV/AIDS',
  'Mental health populations',
  'Substance use disorder recovery',
  'Rural or remote populations',
  'Urban underserved communities',
  'Specific neighborhoods or regions',
  'Companion animals in shelters',
  'Wildlife needing protection or rehabilitation'
];

const organizationSizeOptions = [
  'Just me',
  'Less than 10 employees',
  'Between 11-25 employees',
  'Between 26-50 employees',
  'More than 50 employees'
];

const howHeardOptions = [
  'Referral from a friend or colleague',
  'Nonprofit or sector event/conference',
  'Social media',
  'Email newsletter or campaign',
  'Online Search',
  'Partner organization or network',
  'Through a consultant or vendor',
  'Other'
];



export const OrganizationProfileModal = ({ 
  isOpen, 
  onClose, 
  onSubmit 
}: OrganizationProfileModalProps) => {
  const [customRole, setCustomRole] = useState('');
  const [customTaxStatus, setCustomTaxStatus] = useState('');
  const [customHowHeard, setCustomHowHeard] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset
  } = useForm<OrganizationProfileData>({
    defaultValues: {
      customerName: '',
      customerEmail: '',
      primaryRole: '',
      organizationName: '',
      cityState: '',
      areaOfFocus: [],
      taxExemptStatus: '',
      targetPopulations: [],
      organizationSize: '',
      howHeardAbout: ''
    }
  });

  // Watch specific fields for conditional rendering
  const primaryRole = watch('primaryRole');
  const taxExemptStatus = watch('taxExemptStatus');
  const howHeardAbout = watch('howHeardAbout');

  const onSubmitForm = (data: OrganizationProfileData) => {
    const finalData = {
      ...data,
      primaryRole: data.primaryRole === 'Other' ? customRole : data.primaryRole,
      taxExemptStatus: data.taxExemptStatus === 'Other' ? customTaxStatus : data.taxExemptStatus,
      howHeardAbout: data.howHeardAbout === 'Other' ? customHowHeard : data.howHeardAbout,
    };

    onSubmit?.(finalData);
  };

  const handleClose = () => {
    reset();
    setCustomRole('');
    setCustomTaxStatus('');
    setCustomHowHeard('');
    onClose();
  };

  return (
    <Modal.Wrapper isOpen={isOpen} onClose={handleClose} showCloseButton closeable>
      <div className="flex flex-col h-[80vh]">
        <div className="p-6 pb-2">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Welcome to the First Step
          </h2>
          
          <p className="text-gray-600">
            This brief form is an optional first step in our full nonprofit assessment.
            <span className="flex mt-2">
            Sharing a few basic details about your organization will help us tailor the 
            process to better support your mission.
            </span>
        
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmitForm)} className="flex-1 overflow-y-auto px-6 space-y-4">
          <Controller
            name="customerName"
            control={control}
            rules={{ 
              required: 'Customer name is required',
              validate: value => value.trim() !== '' || 'Customer name cannot be empty'
            }}
            render={({ field }) => (
              <TextInput
                label="Customer Name (required)"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder="Enter your name"
                error={errors.customerName?.message}
              />
            )}
          />

          <Controller
            name="customerEmail"
            control={control}
            rules={{ 
              required: 'Email address is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Please enter a valid email address'
              }
            }}
            render={({ field }) => (
              <TextInput
                label="Customer Email Address (required)"
                type="email"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder="Enter your email address"
                error={errors.customerEmail?.message}
              />
            )}
          />

          <div className="space-y-2">
                         <Controller
               name="primaryRole"
               control={control}
               render={({ field }) => (
                 <SelectInput
                   label="Primary Role within Organization (optional)"
                   value={field.value || ''}
                   onChange={field.onChange}
                   options={roleOptions.map(role => ({ label: role, value: role }))}
                   placeholder="Select your role"
                   error={errors.primaryRole?.message}
                 />
               )}
             />
            {primaryRole === 'Other' && (
              <TextInput
                value={customRole}
                onChange={(e) => setCustomRole(e.target.value)}
                placeholder="Please specify your role"
              />
            )}
          </div>

          <Controller
            name="organizationName"
            control={control}
            rules={{ 
              required: 'Organization name is required',
              validate: value => value.trim() !== '' || 'Organization name cannot be empty'
            }}
            render={({ field }) => (
              <TextInput
                label="Organization Name (required)"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder="Enter organization name"
                error={errors.organizationName?.message}
              />
            )}
          />

          <Controller
            name="cityState"
            control={control}
            rules={{ 
              required: 'City & State is required',
              validate: value => value.trim() !== '' || 'City & State cannot be empty'
            }}
            render={({ field }) => (
              <TextInput
                label="City & State (required)"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder="Enter city & state"
                error={errors.cityState?.message}
              />
            )}
          />

                     <Controller
             name="areaOfFocus"
             control={control}
             render={({ field }) => (
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Organization Area of Focus (optional)
                 </label>
                             <div className="grid grid-cols-2 gap-3 border border-gray-200 rounded-lg p-4 bg-white">
              {focusAreaOptions.map(area => (
                <div key={area} className="flex items-start">
                  <CheckboxInput
                    checked={field.value.includes(area)}
                    onChange={() => {
                      const exists = field.value.includes(area);
                      const newValue = exists 
                        ? field.value.filter((v: string) => v !== area)
                        : [...field.value, area];
                      field.onChange(newValue);
                    }}
                    label={area}
                    wrapperClassName="hover:bg-gray-50 p-2 rounded-md w-full transition-colors"
                  />
                </div>
              ))}
            </div>
                 {errors.areaOfFocus?.message && (
                   <p className="text-red-500 text-sm mt-1">{errors.areaOfFocus.message}</p>
                 )}
               </div>
             )}
           />

          <div className="space-y-2">
                         <Controller
               name="taxExemptStatus"
               control={control}
               render={({ field }) => (
                 <SelectInput
                   label="501(c) Type (optional)"
                   value={field.value || ''}
                   onChange={field.onChange}
                   options={taxStatusOptions.map(status => ({ label: status, value: status }))}
                   placeholder="Select tax-exempt status"
                   error={errors.taxExemptStatus?.message}
                 />
               )}
             />
            {taxExemptStatus === 'Other' && (
              <TextInput
                value={customTaxStatus}
                onChange={(e) => setCustomTaxStatus(e.target.value)}
                placeholder="Please specify"
              />
            )}
          </div>

                     <Controller
             name="targetPopulations"
             control={control}
             rules={{ 
               validate: value => value.length > 0 || 'Please select at least one target population'
             }}
             render={({ field }) => (
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Target Population(s): please check any populations you currently are or plan to support (required)
                 </label>
                             <div className="max-h-[400px] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 border border-gray-200 rounded-lg p-4 bg-white">
                {targetPopulationOptions.map(population => (
                  <div key={population} className="flex items-start">
                    <CheckboxInput
                      checked={field.value.includes(population)}
                      onChange={() => {
                        const exists = field.value.includes(population);
                        const newValue = exists 
                          ? field.value.filter((v: string) => v !== population)
                          : [...field.value, population];
                        field.onChange(newValue);
                      }}
                      label={population}
                      error={errors.targetPopulations?.message}
                      wrapperClassName="hover:bg-gray-50 p-2 rounded-md w-full transition-colors"
                    />
                  </div>
                ))}
              </div>
            </div>
                 {errors.targetPopulations?.message && (
                   <p className="text-red-500 text-sm mt-1">{errors.targetPopulations.message}</p>
                 )}
               </div>
             )}
           />

                     <Controller
             name="organizationSize"
             control={control}
             render={({ field }) => (
               <SelectInput
                 label="Current Size of Organization (optional)"
                 value={field.value || ''}
                 onChange={field.onChange}
                 options={organizationSizeOptions.map(size => ({ label: size, value: size }))}
                 placeholder="Select organization size"
                 error={errors.organizationSize?.message}
               />
             )}
           />

          <div className="space-y-2">
                         <Controller
               name="howHeardAbout"
               control={control}
               render={({ field }) => (
                 <SelectInput
                   label="How did you hear about ViaTelos? (optional)"
                   value={field.value || ''}
                   onChange={field.onChange}
                   options={howHeardOptions.map(option => ({ label: option, value: option }))}
                   placeholder="Select how you heard about us"
                   error={errors.howHeardAbout?.message}
                 />
               )}
             />
            {howHeardAbout === 'Other' && (
              <TextInput
                value={customHowHeard}
                onChange={(e) => setCustomHowHeard(e.target.value)}
                placeholder="Please specify"
              />
            )}
          </div>

        </form>

        <div className="p-6 pt-3 bg-white">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Skip for Now
            </button>
            <LoadingButton type="submit" className="px-6 py-2" onClick={handleSubmit(onSubmitForm)}>
              Finish
            </LoadingButton>
          </div>
        </div>
      </div>
    </Modal.Wrapper>
  );
};